import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Pagination, Table } from "antd";
import { ColumnProps } from "antd/es/table";
import styles from "../../../styles.module.scss";
import { Text } from "@components/ui/Text";
import { Flex } from "@components/ui/Flex";
import { isEmpty } from "lodash";
import { ShowWhenTrue } from "@helpers/showwhentrue";

interface TagItem {
    key: string;
    name: string;
}

interface EditableTagTableProps {
    selectedRowKeys: string[];
    tagPosition: string;
    tags?: string;
    setFieldValue: (field: string, value: string) => void;
}

const PAGE_SIZE_OPTIONS = ['20', '50', '100', '250'];

export const EditableTagTable: React.FC<EditableTagTableProps> = ({
    selectedRowKeys,
    tagPosition,
    tags,
    setFieldValue,
}) => {
    const [dataSource, setDataSource] = useState<TagItem[]>([]);
    const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
    const [editableValues, setEditableValues] = useState<Record<string, string>>({});
    const [originalKeys, setOriginalKeys] = useState<string[]>([]);
    const hasInteracted = useRef(false);
    const markInteracted = () => {
        hasInteracted.current = true;
    };
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    useEffect(() => {
        const newData: TagItem[] = selectedRowKeys.map((key) => ({ key, name: key }));
        setDataSource(newData);
        setEditableValues(Object.fromEntries(newData.map((d) => [d.key, d.name])));
        setCheckedKeys([...selectedRowKeys]);
        setOriginalKeys([...selectedRowKeys]);
    }, [selectedRowKeys]);

    useEffect(() => {
        // Don't wipe restored/manually-entered tags before the user touches the table.
        if (!hasInteracted.current && checkedKeys.length === 0 && tags) return;
        const finalTags = checkedKeys.map((key) => editableValues[key] || key).join(", ");
        setFieldValue("tags", finalTags);
    }, [checkedKeys, editableValues]);


    const handleInputChange = (key: string, value: string) => {
        markInteracted();
        setEditableValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleReset = () => {
        markInteracted();
        const resetEditable = Object.fromEntries(originalKeys.map((k) => [k, k]));
        setEditableValues(resetEditable);
        setCheckedKeys([...originalKeys]);
    };

    const filteredData = useMemo(() => {
        return dataSource.filter((item) =>
            item.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, dataSource]);

    useEffect(() => {
        const totalPages = Math.ceil(filteredData.length / pageSize);
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [filteredData, pageSize]);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredData.slice(startIndex, startIndex + pageSize);
    }, [filteredData, currentPage, pageSize]);

    const leftTableData = paginatedData.filter((_, index) => index % 2 === 0);
    const rightTableData = paginatedData.filter((_, index) => index % 2 !== 0);

    const columns = useMemo<ColumnProps<TagItem>[]>(
        () => [
            {
                key: "name",
                render: (_value, record) =>
                    checkedKeys.includes(record.key) ? (
                        <input
                            value={editableValues[record.key]}
                            className={styles["tag_input"]}
                            onChange={(e) => handleInputChange(record.key, e.target.value)}
                        />
                    ) : (
                        <div>{editableValues[record.key] || record.name}</div>
                    ),
            },
        ],
        [checkedKeys, editableValues]
    );

    const rowSelection = {
        selectedRowKeys: checkedKeys,
        onSelect: (record: TagItem, selected: boolean) => {
            markInteracted();
            setCheckedKeys((prev) =>
                selected
                    ? prev.includes(record.key)
                        ? prev
                        : [...prev, record.key]
                    : prev.filter((k) => k !== record.key)
            );
        },
    };

    const handleSelectAll = (checked: boolean) => {
        markInteracted();
        const keys = filteredData.map((item) => item.key);
        setCheckedKeys(checked ? keys : []);
    };

    return (
        <Flex flexDirection="column" bg="rgb(33, 34, 51)">
            <Flex justifyContent="space-between" alignItems="center" gap="20px" marginBottom="8px" paddingX={30} paddingY={10}>
                <Checkbox
                    className={styles["checkbox-tag"]}
                    checked={checkedKeys.length === filteredData.length && filteredData.length > 0}
                    indeterminate={
                        checkedKeys.length > 0 && checkedKeys.length < filteredData.length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                >
                    Select All
                </Checkbox>
                <input
                    onChange={(e) => setSearch(e.target.value)}
                    className="search_input"
                    placeholder="Search..."
                    value={search}
                />
                <button type="button" onClick={handleReset} className="btn btn-grey btn-center">
                    Reset
                </button>
            </Flex>

            <Flex gap="20px" width={"100%"} paddingX={0} maxHeight={"40vh"} overflowY={"scroll"}>
                <ShowWhenTrue show={!isEmpty(filteredData)}>
                    <ShowWhenTrue show={!isEmpty(leftTableData)}>
                        <div style={{ width: '50%' }}>
                            <Table<TagItem>
                                rowKey="key"
                                rowSelection={rowSelection}
                                className="operator-table"
                                dataSource={leftTableData}
                                columns={columns}
                                showHeader={false}
                                pagination={false}
                                locale={{ emptyText: <Text color="white" fontSize={14}>No Data</Text> }}
                            />
                        </div>
                    </ShowWhenTrue>
                    <ShowWhenTrue show={!isEmpty(rightTableData)}>
                        <div style={{ width: '50%' }}>
                            <Table<TagItem>
                                rowKey="key"
                                rowSelection={rowSelection}
                                className="operator-table"
                                dataSource={rightTableData}
                                columns={columns}
                                showHeader={false}
                                pagination={false}
                                locale={{ emptyText: <Text color="white" fontSize={14}>No Data</Text> }}
                            />
                        </div>
                    </ShowWhenTrue>
                </ShowWhenTrue>
            </Flex>
            <Flex justifyContent="flex-end" paddingBottom={10}>
                {!isEmpty(filteredData) && <Pagination
                    key={`${currentPage}-${pageSize}`}
                    current={currentPage}
                    pageSize={pageSize}
                    total={filteredData.length}
                    showSizeChanger
                    onShowSizeChange={(page, size) => {
                        setPageSize(size || 20);
                    }}
                    onChange={(page, size) => {
                        setCurrentPage(page);
                    }}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    className={styles.pagination}
                />}
            </Flex>
        </Flex>
    );
};